import { dateUtils } from '@valid-eval/shared-react-components';
import moment from 'moment-timezone';

import { Phase } from 'data/features/phaseTypes';
import { PhaseFormData } from './types';
import { getSupportEmail } from 'config';
import i18n from 'config/i18n';

const { formValueToIso, formValueToDate, DATE_FORMAT_WITH_TIME } = dateUtils;
const email = getSupportEmail();

/**
 * Transforms phase form data into a Phase object suitable for API submission.
 * Handles date format conversions and artifact ID processing.
 *
 * @param {PhaseFormData} data - The raw form data from the phase form
 * @returns {Phase} The transformed phase object with properly formatted dates and artifact IDs
 */
export const transformPhaseFormValues = (data: PhaseFormData): Phase => {
  const { artifact_ids_checkboxes, ...rest } = data;
  return {
    ...rest,
    start_date: formValueToIso(data.start_date),
    end_date: formValueToIso(data.end_date),
    consensus_meeting_date: formValueToDate(data.consensus_meeting_date),
    feedback_available_date: formValueToDate(data.feedback_available_date),
    artifact_ids: artifact_ids_checkboxes?.filter(Boolean).map(Number) || [],
  } as Phase;
};

/**
 * Validates that the team signup open date falls within acceptable bounds.
 * The date must be:
 * - On or after the event start date
 * - On or before the event end date
 * - On or before the team signup deadline (if provided)
 *
 * @param {string | moment.Moment} value - The team signup open date to validate
 * @param {string | undefined} rawTeamSignupDeadline - Optional raw team signup deadline string
 * @param {moment.Moment} startDate - The event start date
 * @param {moment.Moment} endDate - The event end date
 * @returns {string | undefined} Error message if validation fails, undefined if valid
 */
export const validateTeamSignupOpen = (
  value: string | moment.Moment,
  rawTeamSignupDeadline: string | undefined,
  startDate: moment.Moment,
  endDate: moment.Moment,
) => {
  if (!value) return;

  const selectedDate = moment(value, DATE_FORMAT_WITH_TIME);
  const teamSignupDeadline =
    rawTeamSignupDeadline && rawTeamSignupDeadline.length > DATE_FORMAT_WITH_TIME.length
      ? moment(rawTeamSignupDeadline, DATE_FORMAT_WITH_TIME)
      : null;

  const isAfterStartDate = selectedDate.isSameOrAfter(startDate);
  const isBeforeEndDate = selectedDate.isSameOrBefore(endDate);
  const isBeforeTeamSignupDeadline = teamSignupDeadline?.isValid()
    ? selectedDate.isSameOrBefore(teamSignupDeadline)
    : true;

  if (!isAfterStartDate) {
    return i18n.t('settings.error.sign_up_open_less', {
      date: startDate.format(DATE_FORMAT_WITH_TIME),
      email,
    });
  }
  if (!isBeforeEndDate) {
    return i18n.t('settings.error.sign_up_open_greater', {
      date: endDate.format(DATE_FORMAT_WITH_TIME),
      email,
    });
  }
  if (!isBeforeTeamSignupDeadline && teamSignupDeadline) {
    return i18n.t('settings.error.sign_up_open_greater', {
      date: teamSignupDeadline.format(DATE_FORMAT_WITH_TIME),
      email,
    });
  }
  return;
};

/**
 * Validates that the team signup deadline falls within acceptable bounds.
 * The date must be:
 * - On or after the event start date
 * - On or after the team signup open date (if provided)
 * - On or before the event end date
 *
 * @param {string | moment.Moment} value - The team signup deadline to validate
 * @param {string | undefined} rawTeamSignupOpen - Optional raw team signup open date string
 * @param {moment.Moment} startDate - The event start date
 * @param {moment.Moment} endDate - The event end date
 * @returns {string | undefined} Error message if validation fails, undefined if valid or no value provided
 */
export const validateTeamSignupDeadline = (
  value: string | moment.Moment,
  rawTeamSignupOpen: string | undefined,
  startDate: moment.Moment,
  endDate: moment.Moment,
) => {
  if (!value) return;

  if (!rawTeamSignupOpen) {
    return i18n.t('settings.error.sign_up_deadline_empty_sign_up_open', {
      email,
    });
  }

  const selectedDate = moment(value, DATE_FORMAT_WITH_TIME);
  const teamSignupOpen = moment(rawTeamSignupOpen, DATE_FORMAT_WITH_TIME);

  const isAfterStartDate = selectedDate.isSameOrAfter(startDate);
  const isAfterteamSignupOpen = teamSignupOpen ? selectedDate.isSameOrAfter(teamSignupOpen) : true;
  const isBeforeEndDate = selectedDate.isSameOrBefore(endDate);

  if (!isAfterStartDate) {
    return i18n.t('settings.error.sign_up_deadline_less', {
      date: startDate.format(DATE_FORMAT_WITH_TIME),
      email,
    });
  }
  if (!isAfterteamSignupOpen && teamSignupOpen) {
    return i18n.t('settings.error.sign_up_deadline_less', {
      date: teamSignupOpen.format(DATE_FORMAT_WITH_TIME),
      email,
    });
  }
  if (!isBeforeEndDate) {
    return i18n.t('settings.error.sign_up_deadline_greater', {
      date: endDate.format(DATE_FORMAT_WITH_TIME),
      email,
    });
  }
  return;
};

/**
 * Validates that a phase start date falls within acceptable bounds.
 * The date must be:
 * - On or after the event start date
 * - On or before the event end date
 * - On or before the phase end date (if provided)
 *
 * @param {string | moment.Moment} value - The phase start date to validate
 * @param {string | undefined} rawPhaseEndDate - Optional raw phase end date string
 * @param {moment.Moment} eventStartDate - The event start date
 * @param {moment.Moment} eventEndDate - The event end date
 * @returns {string | undefined} Error message if validation fails, undefined if valid
 */
export const validatePhaseStartDate = (
  value: string | moment.Moment,
  rawPhaseEndDate: string | undefined,
  eventStartDate: moment.Moment,
  eventEndDate: moment.Moment,
) => {
  const selectedDate = moment(value, DATE_FORMAT_WITH_TIME);
  const phaseEndDate =
    rawPhaseEndDate && rawPhaseEndDate.length > DATE_FORMAT_WITH_TIME.length
      ? moment(rawPhaseEndDate, DATE_FORMAT_WITH_TIME)
      : null;

  const isAfterEventStartDate = selectedDate.isSameOrAfter(eventStartDate);
  const isBeforeEventEndDate = selectedDate.isSameOrBefore(eventEndDate);
  const isBeforePhaseEndDate = phaseEndDate ? selectedDate.isSameOrBefore(phaseEndDate) : true;

  if (!isAfterEventStartDate) {
    return i18n.t('settings.error.phase_start_less', {
      date: eventStartDate.format(DATE_FORMAT_WITH_TIME),
      email,
    });
  }
  if (!isBeforeEventEndDate) {
    return i18n.t('settings.error.phase_start_greater', {
      date: eventEndDate.format(DATE_FORMAT_WITH_TIME),
      email,
    });
  }
  if (!isBeforePhaseEndDate && phaseEndDate) {
    return i18n.t('settings.error.phase_start_greater', {
      date: phaseEndDate.format(DATE_FORMAT_WITH_TIME),
      email,
    });
  }
  return;
};

/**
 * Validates that a phase end date falls within acceptable bounds.
 * The date must be:
 * - On or after the event start date
 * - On or after the phase start date (if provided)
 * - On or before the event end date
 *
 * @param {string | moment.Moment} value - The phase end date to validate
 * @param {string | undefined} rawPhaseStartDate - Optional raw phase start date string
 * @param {moment.Moment} eventStartDate - The event start date
 * @param {moment.Moment} eventEndDate - The event end date
 * @returns {string | undefined} Error message if validation fails, undefined if valid
 */
export const validatePhaseEndDate = (
  value: string | moment.Moment,
  rawPhaseStartDate: string | undefined,
  eventStartDate: moment.Moment,
  eventEndDate: moment.Moment,
) => {
  const selectedDate = moment(value, DATE_FORMAT_WITH_TIME);
  const phaseStartDate =
    rawPhaseStartDate && rawPhaseStartDate.length > DATE_FORMAT_WITH_TIME.length
      ? moment(rawPhaseStartDate, DATE_FORMAT_WITH_TIME)
      : null;

  const isAfterEventStartDate = selectedDate.isSameOrAfter(eventStartDate);
  const isAfterPhaseStartDate = phaseStartDate ? selectedDate.isSameOrAfter(phaseStartDate) : true;
  const isBeforeEventEndDate = selectedDate.isSameOrBefore(eventEndDate);

  if (!isAfterEventStartDate) {
    return i18n.t('settings.error.phase_end_less', {
      date: eventStartDate.format(DATE_FORMAT_WITH_TIME),
      email,
    });
  }
  if (!isAfterPhaseStartDate && phaseStartDate) {
    return i18n.t('settings.error.phase_end_less', {
      date: phaseStartDate.format(DATE_FORMAT_WITH_TIME),
      email,
    });
  }
  if (!isBeforeEventEndDate) {
    return i18n.t('settings.error.phase_end_greater', {
      date: eventEndDate.format(DATE_FORMAT_WITH_TIME),
      email,
    });
  }
  return;
};
